import {
  Alert,
  Box,
  Button,
  Collapse,
  FormControlLabel,
  Link,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { useLocalStorageValue } from "@react-hookz/web";
import { useCallback, useState } from "react";
import { Link as RouterLink } from "react-router";
import { makeDefaultFilters } from "../../../utils/default-filters";
import { PAGE_SIZE, useRecentlyEdited } from "../hooks/use-recently-edited";
import { AssetRow } from "./recently-edited/asset-row";
import { CommunicationRow } from "./recently-edited/communication-row";
import { RecentlyEditedEmpty } from "./recently-edited/recently-edited-empty";
import { SurveyRow } from "./recently-edited/survey-row";

import type { RecentlyEditedItem, RecentlyEditedType } from "../hooks/use-recently-edited";
import type { FC } from "react";

const TABS: { value: RecentlyEditedType | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "communication", label: "Communications" },
  { value: "survey", label: "Surveys" },
  { value: "digital_asset", label: "Content Library" },
];

const ItemRow: FC<{
  item: RecentlyEditedItem;
  onDuplicated: (copy: RecentlyEditedItem) => void;
}> = ({ item, onDuplicated }) => {
  switch (item.entity_type) {
    case "communication":
      return <CommunicationRow communication={item} />;
    case "survey":
      return <SurveyRow survey={item} />;
    case "digital_asset":
      return <AssetRow asset={item} onDuplicated={onDuplicated} />;
    default:
      return null;
  }
};

/** The Communications, Surveys and Digital Assets most recently edited, to pick up where left. */
export const RecentlyEditedSection: FC = () => {
  // Remembered in this browser; react-hookz falls back on the default where storage is blocked.
  const { value: mine = true, set: setMine } = useLocalStorageValue<boolean>(
    "dashboard-recently-edited-mine",
    { defaultValue: true },
  );
  // Not remembered: the dashboard always opens on All.
  const [tab, setTab] = useState<RecentlyEditedType | "all">("all");
  const {
    items,
    reloading,
    error,
    reload,
    hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
    prepend,
  } = useRecentlyEdited(mine, tab === "all" ? undefined : tab);

  // Copies made here slide open at the top; everything else just appears.
  const [sliding, setSliding] = useState<ReadonlySet<string>>(new Set());
  const onDuplicated = useCallback(
    (copy: RecentlyEditedItem) => {
      setSliding((ids) => new Set(ids).add(copy.id));
      prepend(copy);
    },
    [prepend],
  );
  const slid = useCallback(
    (id: string) =>
      setSliding((ids) => {
        const rest = new Set(ids);
        rest.delete(id);
        return rest;
      }),
    [],
  );

  return (
    <Box component="section">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Typography variant="h6" component="h2">
          Recently edited
        </Typography>
        <Box sx={{ display: "flex", gap: 3 }}>
          <Link
            component={RouterLink}
            to={`/calendars/schedule?${makeDefaultFilters().toString()}`}
            variant="subtitle2"
            underline="hover"
            data-analytics-id="dashboard-recently-edited-upcoming-sends"
          >
            Upcoming Sends
          </Link>
          <Link
            component={RouterLink}
            to="/moments/history"
            variant="subtitle2"
            underline="hover"
            data-analytics-id="dashboard-recently-edited-sent-communications"
          >
            Sent Communications
          </Link>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          columnGap: 2,
          flexWrap: "wrap",
          my: 1,
        }}
      >
        <Tabs
          value={tab}
          onChange={(_, value: RecentlyEditedType | "all") => setTab(value)}
          aria-label="Recently edited content"
          variant="scrollable"
          scrollButtons={false}
        >
          {TABS.map(({ value, label }) => (
            <Tab
              key={value}
              value={value}
              label={label}
              data-analytics-id={`dashboard-recently-edited-tab-${label.toLowerCase().replaceAll(" ", "-")}`}
            />
          ))}
        </Tabs>
        <FormControlLabel
          label="My Content only"
          labelPlacement="start"
          control={
            <Switch
              size="small"
              checked={mine}
              onChange={(event) => setMine(event.target.checked)}
              data-analytics-id="dashboard-recently-edited-mine"
            />
          }
        />
      </Box>

      {error ? (
        <Alert
          severity="error"
          action={
            <Button
              color="inherit"
              size="small"
              onClick={reload}
              data-analytics-id="dashboard-recently-edited-retry"
            >
              Try again
            </Button>
          }
        >
          Recently edited content couldn't be loaded.
        </Alert>
      ) : items === null ? (
        <Stack spacing={1.5}>
          {/* As many as the first page, so the rows land where their placeholders were. */}
          {Array.from({ length: PAGE_SIZE }, (_, index) => (
            <Skeleton key={index} variant="rounded" height={88} />
          ))}
        </Stack>
      ) : (
        // Faded while a change of filter loads, over what was there, rather than placeholders.
        <Box
          aria-busy={reloading}
          sx={(theme) => ({
            opacity: reloading ? 0.6 : 1,
            transition: theme.transitions.create("opacity"),
          })}
        >
          {items.length === 0 ? (
            <RecentlyEditedEmpty entityType={tab === "all" ? undefined : tab} />
          ) : (
            <>
              {/* Each row brings its own gap below, so a sliding one opens its gap with it. */}
              <Box component="ul" sx={{ listStyle: "none", m: 0, mb: -1.5, p: 0 }}>
                {items.map((item) => (
                  <Collapse
                    key={item.id}
                    component="li"
                    in
                    appear={sliding.has(item.id)}
                    onEntered={() => slid(item.id)}
                  >
                    <Box sx={{ pb: 1.5 }}>
                      <ItemRow item={item} onDuplicated={onDuplicated} />
                    </Box>
                  </Collapse>
                ))}
              </Box>
              {(hasMore || loadMoreError) && (
                <Box sx={{ textAlign: "center", mt: 2 }}>
                  {loadMoreError && (
                    <Typography variant="body2" sx={{ color: "error.main", mb: 1 }}>
                      Couldn't load more.
                    </Typography>
                  )}
                  <Button
                    onClick={() => void loadMore()}
                    loading={loadingMore}
                    disabled={reloading}
                    data-analytics-id="dashboard-recently-edited-load-more"
                  >
                    Load more
                  </Button>
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Box>
  );
};
